<template>
  <div>
    <div class="row">
      <div class="col">
        <button @click="goBack()" class="btn btn-secondary m-3">Back</button>
      </div>
    </div>

    <div class="row align-items-center m-3">
      <div class="col">
        <p class="h1 t-race">{{ getReportName }}</p>
      </div>
    </div>

    <div class="dropdown show m-3">
      <a
        class="btn btn-secondary dropdown-toggle"
        @click="toggleDropdown()"
        href="#"
        role="button"
      >
        EXPORTS
      </a>

      <div v-bind:class="dropdownToggle">
        <a @click="openLink('CSV')" class="dropdown-item" href="#">CSV</a>
        <a @click="openLink('XL')" class="dropdown-item" href="#">Excel</a>
      </div>
    </div>

    <div class="sectional-info-wrapper">
      <div class="legend">
        <span><i class="rank-one"></i>Fastest</span>
        <span><i class="rank-two"></i>2nd fastest</span>
        <span><i class="rank-three"></i>3rd fastest</span>
      </div>

      <div class="table-responsive">
        <table class="table sectional-table">
          <thead>
            <tr>
              <th rowspan="2">Horse</th>
              <th rowspan="2">Weight</th>
              <th rowspan="2">No.</th>

              <th v-for="section in sections" :key="section" colspan="2">
                {{ section }}
              </th>

              <th rowspan="2">Max Speed</th>
              <th rowspan="2">Dist.</th>
              <th rowspan="2">Time</th>
            </tr>

            <tr>
              <th v-for="column in sectionColumns" :key="column.key">
                {{ column.label }}
              </th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="horse in horses"
              :key="
                getHorseNumber(horse) + '-' + getValue(horse, 'horse', 'Horse')
              "
            >
              <td class="horse-name">
                {{ getValue(horse, "horse", "Horse") }}
                <span class="jockeyAndTrainer">
                  {{ getValue(horse, "jockey", "Jockey") }} /
                  {{ getValue(horse, "trainer", "Trainer") }}</span
                >
              </td>
              <td class="horse-weight">
                <div class="weight-content">
                  {{ getValue(horse, "weight", "Weight") }}
                  <span class="allowed-weight">
                    {{
                      getValue(horse, "weightAllowed", "WeightAllowed") === 0
                        ? getValue(horse, "weightAllowed", "WeightAllowed")
                        : `- ${getValue(
                            horse,
                            "weightAllowed",
                            "WeightAllowed",
                          )}`
                    }}
                  </span>
                </div>
              </td>

              <td>
                {{ getHorseNumber(horse) }}
              </td>

              <td
                v-for="column in sectionColumns"
                :key="column.key + '-' + getHorseNumber(horse)"
                :class="getSectionRankClass(horse, column.section)"
              >
                {{ getSectionValue(horse, column.section)[column.type] }}
              </td>

              <td>
                {{ formatNumber(getValue(horse, "maxSpeed", "MaxSpeed")) }}
              </td>
              <td>
                {{
                  formatNumber(
                    getValue(horse, "totalDistance", "TotalDistance"),
                  )
                }}
              </td>
              <td>
                {{ formatNumber(getValue(horse, "totalTime", "TotalTime")) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!horses.length" class="no-data">No sectional info found.</div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { config } from "../../config";

export default {
  name: "SectionalTimeRankReport",

  data() {
    return {
      dropdownToggle: "dropdown-menu",
      dropdownStatus: false,
    };
  },

  computed: {
    ...mapGetters(["getReportSectional", "getReportName"]),

    reportData() {
      return (
        this.getReportSectional || {
          resultData: [],
          ResultData: [],
          sections: [],
          Sections: [],
        }
      );
    },

    horses() {
      return this.reportData.resultData || this.reportData.ResultData || [];
    },

    sections() {
      return this.reportData.sections || this.reportData.Sections || [];
    },

    sectionColumns() {
      return this.sections.reduce((columns, section) => {
        columns.push({
          key: section + "-rank",
          section: section,
          label: "Rank",
          type: "rank",
        });

        columns.push({
          key: section + "-time",
          section: section,
          label: "Time",
          type: "time",
        });

        return columns;
      }, []);
    },
  },

  methods: {
    goBack() {
      this.$router.push("/landing/SectionalTimeRank");
    },

    getValue(item, camelKey, pascalKey) {
      if (!item) return "";
      return item[camelKey] !== undefined ? item[camelKey] : item[pascalKey];
    },

    getHorseNumber(horse) {
      return this.getValue(horse, "horseNumber", "HorseNumber");
    },

    getSectionValue(horse, sectionName) {
      const horseSections = this.getValue(horse, "sections", "Sections") || [];

      const section = horseSections.find((item) => {
        const name = this.getValue(item, "sectionName", "SectionName");
        return name === sectionName;
      });

      if (!section) {
        return {
          rank: "",
          time: "",
        };
      }

      return {
        rank: this.getValue(section, "rank", "Rank"),
        time: this.formatNumber(this.getValue(section, "time", "Time")),
      };
    },

    getSectionRankClass(horse, sectionName) {
      const sectionTimes = this.horses
        .map((item) => {
          const section = this.getSectionValue(item, sectionName);

          return Number(section.time);
        })
        .filter((time) => !Number.isNaN(time))
        .sort((a, b) => a - b);

      const uniqueTimes = [...new Set(sectionTimes)];

      const currentTime = Number(this.getSectionValue(horse, sectionName).time);

      const rankIndex = uniqueTimes.indexOf(currentTime);

      if (rankIndex === 0) return "rank-one-bg";
      if (rankIndex === 1) return "rank-two-bg";
      if (rankIndex === 2) return "rank-three-bg";

      return "";
    },

    formatNumber(value) {
      if (value === null || value === undefined || value === "") {
        return "";
      }

      const number = Number(value);

      if (Number.isNaN(number)) {
        return value;
      }

      return Number.isInteger(number) ? number : number.toFixed(2);
    },

    toggleDropdown() {
      this.dropdownStatus = !this.dropdownStatus;
      this.dropdownToggle = this.dropdownStatus
        ? "dropdown-menu show"
        : "dropdown-menu";
    },

    openLink(fileType) {
      const firstHorse = this.horses[0];

      const raceCourse = this.getValue(
        firstHorse,
        "raceCourseCode",
        "RaceCourseCode",
      );

      const raceNumber = this.getValue(firstHorse, "raceNumber", "RaceNumber");

      const meetingDate = this.getValue(
        firstHorse,
        "meetingDate",
        "MeetingDate",
      );

      window.open(
        `${config.reportingURL}/api/Reports/GetSectionalTimeRankFile?rc=${raceCourse}&startDate=${meetingDate}&rn=${raceNumber}&fileType=${fileType}`,
        "_blank",
      );
    },
  },
};
</script>

<style scoped>
.sectional-info-wrapper {
  background: black;
  color: white;
  padding: 1rem;
  width: 99%;
  margin-inline: auto;
}

.legend {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.legend span {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.legend i {
  width: 18px;
  height: 18px;
  border-radius: 6px;
  display: inline-block;
}

.rank-one,
.rank-one-bg {
  background: #ff3b30 !important;
}

.rank-two,
.rank-two-bg {
  background: #ff8c2a !important;
}

.rank-three,
.rank-three-bg {
  background: #f4ff20 !important;
  color: black !important;
}

.sectional-table {
  color: white;
  background: black;
  font-size: 0.85rem;
}

.sectional-table th,
.sectional-table td {
  border: 1px solid #343a40;
  text-align: center;
  padding: 0.35rem;
  white-space: nowrap;
}

.sectional-table th {
  background: black;
  color: white;
  font-weight: bold;
}

.horse-name {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
  text-align: left !important;
}

.jockeyAndTrainer,
.allowed-weight {
  font-size: 0.7rem;
  font-style: italic;
}
.horse-weight {
  text-align: center !important;
}
.weight-content {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

.no-data {
  padding: 1rem;
  text-align: center;
  color: white;
}

.t-race {
  text-align: center;
}
</style>
